import { useState, useEffect } from "react";
import { getCapacity, updateCapacity, type CapacityData } from "../../api/spaces.api";

export default function Espacios() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [capacityData, setCapacityData] = useState<CapacityData | null>(null);
  
  // Campos del formulario con toggles
  const [aceptaCarros, setAceptaCarros] = useState(false);
  const [aceptaMotos, setAceptaMotos] = useState(false);
  const [formCarros, setFormCarros] = useState<string>("");
  const [formMotos, setFormMotos] = useState<string>("");

  useEffect(() => {
    loadCapacity();
  }, []);

  const loadCapacity = async () => {
    setIsLoading(true);
    try {
      const data = await getCapacity();
      setCapacityData(data);
      
      // Inicializar toggles basado en la capacidad existente
      const carrosActivos = data.capacidadCarros > 0;
      const motosActivos = data.capacidadMotos > 0;
      
      setAceptaCarros(carrosActivos);
      setAceptaMotos(motosActivos);
      setFormCarros(carrosActivos ? data.capacidadCarros.toString() : "50");
      setFormMotos(motosActivos ? data.capacidadMotos.toString() : "30");
    } catch (err) {
      console.error('Error cargando capacidad:', err);
      setError('Error al cargar la configuración de capacidad');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const data = await updateCapacity({
        capacidadCarros: aceptaCarros ? (parseInt(formCarros) || 0) : 0,
        capacidadMotos: aceptaMotos ? (parseInt(formMotos) || 0) : 0,
      });
      setCapacityData(data);
      setSuccessMessage('Configuración guardada exitosamente');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error guardando capacidad:', err);
      setError('Error al guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const getOccupancyColor = (percentage: number) => {
    if (percentage >= 90) return { bg: "bg-rose-500", text: "text-rose-600", light: "bg-rose-100" };
    if (percentage >= 70) return { bg: "bg-amber-500", text: "text-amber-600", light: "bg-amber-100" };
    return { bg: "bg-emerald-500", text: "text-emerald-600", light: "bg-emerald-100" };
  };

  const getCapacityLabel = () => {
    if (aceptaCarros && aceptaMotos) return "Parqueadero mixto (Carros y Motos)";
    if (aceptaCarros) return "Parqueadero solo para Carros";
    if (aceptaMotos) return "Parqueadero solo para Motos";
    return "Sin tipos de vehículo configurados";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  const capacidadCarros = capacityData?.capacidadCarros || 0;
  const capacidadMotos = capacityData?.capacidadMotos || 0;
  const ocupacionCarros = capacityData?.ocupacionCarros || 0;
  const ocupacionMotos = capacityData?.ocupacionMotos || 0;
  const porcentajeCarros = capacityData?.porcentajeCarros || 0;
  const porcentajeMotos = capacityData?.porcentajeMotos || 0;
  const porcentajeTotal = capacityData?.porcentajeOcupacion || 0;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Configuración de Capacidad</h1>
          <p className="text-sm text-gray-500 mt-1">Define la cantidad de espacios disponibles en tu parqueadero</p>
        </div>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl flex items-center gap-2">
          <i className="ri-error-warning-line text-xl"></i>
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-xl"></i>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Tarjetas de Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Capacidad Total */}
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Capacidad Total</p>
              <p className="text-3xl font-bold text-gray-900">{capacityData?.capacidadTotal || 0}</p>
            </div>
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center">
              <i className="ri-parking-box-line text-2xl text-blue-600"></i>
            </div>
          </div>
          <p className="text-xs text-gray-400">{getCapacityLabel()}</p>
        </div>

        {/* Ocupados */}
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Ocupados Actualmente</p>
              <p className="text-3xl font-bold text-gray-900">{capacityData?.ocupacionTotal || 0}</p>
            </div>
            <div className={`w-14 h-14 ${getOccupancyColor(porcentajeTotal).light} rounded-xl flex items-center justify-center`}>
              <i className={`ri-car-fill text-2xl ${getOccupancyColor(porcentajeTotal).text}`}></i>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getOccupancyColor(porcentajeTotal).bg} rounded-full transition-all duration-500`}
                style={{ width: `${Math.min(porcentajeTotal, 100)}%` }}
              ></div>
            </div>
            <span className={`text-sm font-semibold ${getOccupancyColor(porcentajeTotal).text}`}>{porcentajeTotal}%</span>
          </div>
        </div>

        {/* Disponibles */}
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Espacios Disponibles</p>
              <p className="text-3xl font-bold text-emerald-600">{capacityData?.disponiblesTotal || 0}</p>
            </div>
            <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-2xl text-emerald-600"></i>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            {(capacityData?.disponiblesTotal || 0) === 0 ? "¡Parqueadero lleno!" : "Listos para recibir vehículos"}
          </p>
        </div>
      </div>

      {/* Configuración y Detalle por Tipo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Formulario de Configuración */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-settings-3-line text-teal-600"></i>
            Tipos de Vehículos Aceptados
          </h2>
          
          <div className="space-y-6">
            {/* Toggle Carros */}
            <div className={`p-5 rounded-xl border-2 transition-all ${aceptaCarros ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${aceptaCarros ? 'bg-blue-100' : 'bg-gray-200'}`}>
                    <i className={`ri-car-line text-xl ${aceptaCarros ? 'text-blue-600' : 'text-gray-400'}`}></i>
                  </div>
                  <div>
                    <p className={`font-semibold ${aceptaCarros ? 'text-gray-900' : 'text-gray-500'}`}>Carros</p>
                    <p className="text-xs text-gray-400">Auto, Camioneta, Camión</p>
                  </div>
                </div>
                
                {/* Toggle Switch */}
                <button
                  type="button"
                  onClick={() => setAceptaCarros(!aceptaCarros)}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${aceptaCarros ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span 
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${aceptaCarros ? 'translate-x-5' : 'translate-x-0'}`}
                  ></span>
                </button>
              </div>
              
              {/* Campo de capacidad (solo si está activo) */}
              {aceptaCarros && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad de espacios
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const currentVal = parseInt(formCarros) || 0;
                        if (currentVal > 1) {
                          setFormCarros(String(currentVal - 1));
                        }
                      }}
                      className="w-12 h-12 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-all active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={parseInt(formCarros) <= 1}
                    >
                      <i className="ri-subtract-line text-2xl"></i>
                    </button>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formCarros}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) {
                                setFormCarros(val);
                            }
                        }}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-2xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                      <span className="hidden sm:block absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">espacios</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const currentVal = parseInt(formCarros) || 0;
                        setFormCarros(String(currentVal + 1));
                      }}
                      className="w-12 h-12 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-all active:scale-95 shadow-md"
                    >
                      <i className="ri-add-line text-2xl"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Toggle Motos */}
            <div className={`p-5 rounded-xl border-2 transition-all ${aceptaMotos ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${aceptaMotos ? 'bg-emerald-100' : 'bg-gray-200'}`}>
                    <i className={`ri-motorbike-line text-xl ${aceptaMotos ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                  </div>
                  <div>
                    <p className={`font-semibold ${aceptaMotos ? 'text-gray-900' : 'text-gray-500'}`}>Motos</p>
                    <p className="text-xs text-gray-400">Moto, Bicicleta</p>
                  </div>
                </div>
                
                {/* Toggle Switch */}
                <button
                  type="button"
                  onClick={() => setAceptaMotos(!aceptaMotos)}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${aceptaMotos ? 'bg-emerald-600' : 'bg-gray-300'}`}
                >
                  <span 
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${aceptaMotos ? 'translate-x-5' : 'translate-x-0'}`}
                  ></span>
                </button>
              </div>
              
              {/* Campo de capacidad (solo si está activo) */}
              {aceptaMotos && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad de espacios
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const currentVal = parseInt(formMotos) || 0;
                        if (currentVal > 1) {
                          setFormMotos(String(currentVal - 1));
                        }
                      }}
                      className="w-12 h-12 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-all active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={parseInt(formMotos) <= 1}
                    >
                      <i className="ri-subtract-line text-2xl"></i>
                    </button>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formMotos}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) {
                                setFormMotos(val);
                            }
                        }}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-2xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                      />
                      <span className="hidden sm:block absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">espacios</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const currentVal = parseInt(formMotos) || 0;
                        setFormMotos(String(currentVal + 1));
                      }}
                      className="w-12 h-12 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-all active:scale-95 shadow-md"
                    >
                      <i className="ri-add-line text-2xl"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Advertencia si no hay ninguno activo */}
            {!aceptaCarros && !aceptaMotos && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex gap-3">
                  <i className="ri-alert-line text-amber-500 text-xl flex-shrink-0"></i>
                  <div className="text-sm text-amber-700">
                    <p className="font-medium">Sin tipos de vehículo activos</p>
                    <p className="text-amber-600">Activa al menos un tipo para registrar entradas.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Botón Guardar */}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full px-6 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Guardar Configuración
                </>
              )}
            </button>
          </div>
        </div>

        {/* Detalle por Tipo */}
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-pie-chart-line text-teal-600"></i>
            Ocupación por Tipo
          </h2>
          
          <div className="space-y-6">
            {/* Carros */}
            {capacidadCarros > 0 && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-car-line text-blue-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Carros</p>
                      <p className="text-xs text-gray-500">Auto, Camioneta, Camión</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{ocupacionCarros} / {capacidadCarros}</p>
                    <p className="text-xs text-gray-500">{capacityData?.disponiblesCarros} disponibles</p>
                  </div>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getOccupancyColor(porcentajeCarros).bg} rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(porcentajeCarros, 100)}%` }}
                  ></div>
                </div>
                <p className={`text-right text-sm font-semibold mt-1 ${getOccupancyColor(porcentajeCarros).text}`}>
                  {porcentajeCarros}% ocupado
                </p>
              </div>
            )}

            {/* Motos */}
            {capacidadMotos > 0 && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <i className="ri-motorbike-line text-emerald-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Motos</p>
                      <p className="text-xs text-gray-500">Moto, Bicicleta</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{ocupacionMotos} / {capacidadMotos}</p>
                    <p className="text-xs text-gray-500">{capacityData?.disponiblesMotos} disponibles</p>
                  </div>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getOccupancyColor(porcentajeMotos).bg} rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(porcentajeMotos, 100)}%` }}
                  ></div>
                </div>
                <p className={`text-right text-sm font-semibold mt-1 ${getOccupancyColor(porcentajeMotos).text}`}>
                  {porcentajeMotos}% ocupado
                </p>
              </div>
            )}

            {/* Sin capacidad configurada */}
            {capacidadCarros === 0 && capacidadMotos === 0 && (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-parking-box-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-gray-600 font-medium">Sin capacidad configurada</p>
                <p className="text-sm text-gray-400 mt-1">Activa los tipos de vehículo y guarda la configuración</p>
              </div>
            )}

            {/* Indicadores de estado */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-3">Indicadores de estado</p>
              <div className="flex gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-xs text-gray-600">Disponible (&lt;70%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-xs text-gray-600">Casi lleno (70-90%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                  <span className="text-xs text-gray-600">Lleno (&gt;90%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
